import { sansPrefixeNiveau } from '../components/SelecteurContenu.tsx';

/**
 * L'emploi du temps imprimé — repris de la v1.
 *
 * ── Pourquoi pas une copie de l'écran ───────────────────────────────────────
 * Le planning de l'écran est une journée dessinée à l'échelle, de 7 h à 19 h.
 * Copié sur une feuille, il gardait cinq heures vides, coupait les libellés
 * (« Enseignements artisti… ») et perdait le sous-domaine faute de place. La
 * v1 imprimait autre chose, et mieux : un TABLEAU dont les lignes sont les
 * seuls horaires réellement utilisés.
 *
 * ── La trame ────────────────────────────────────────────────────────────────
 * Toutes les bornes rencontrées — débuts ET fins — découpent la journée en
 * bandes. Un créneau occupe, par fusion de cellules, toutes les bandes qu'il
 * couvre. Une trame faite des seuls couples début/fin laissait des trous dès
 * que deux jours partageaient un début mais pas une fin.
 *
 * ── La hauteur ──────────────────────────────────────────────────────────────
 * Proportionnelle à la durée, comme à l'écran — une heure prend quatre fois la
 * place d'un quart d'heure —, avec un plancher qui garde deux horaires
 * lisibles, et une somme ramenée à une page A4 paysage.
 */

export interface CreneauImprime {
  weekday: number;
  startsAt: string;
  endsAt: string;
  label: string;
  subjectName: string;
  subjectAreaText: string;
  color: string;
}

const NOMS_JOURS = ['', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

/** A4 paysage : 190 mm utiles, moins le titre et la ligne d'en-tête. */
const HAUTEUR_MM = 160;
/** Ce qu'il faut à une bande pour porter ses deux horaires. */
const PLANCHER_MM = 8.6;

const minutes = (h: string) => {
  const [hh, mm] = h.split(':').map(Number);
  return (hh ?? 0) * 60 + (mm ?? 0);
};
const heure = (h: string) => `${Number(h.slice(0, 2))}h${h.slice(3, 5)}`;

export function echapperHtml(t: string): string {
  return t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Hauteur de chaque bande, en mm : sa part de durée, jamais sous le plancher. */
export function hauteursDesBandes(durees: number[]): number[] {
  const nb = Math.max(1, durees.length);
  const plancher = Math.min(PLANCHER_MM, HAUTEUR_MM / nb);
  const hauteurs = new Array<number>(durees.length).fill(0);
  let libres = durees.map((_, i) => i);
  let reste = HAUTEUR_MM;
  // Ce que les bandes courtes prennent en trop est repris aux longues, jusqu'à
  // ce que la somme fasse exactement la page.
  for (let garde = 0; garde < 30 && libres.length; garde += 1) {
    const total = libres.reduce((t, i) => t + durees[i]!, 0) || 1;
    const courtes = libres.filter((i) => (durees[i]! / total) * reste < plancher);
    if (courtes.length === 0) {
      for (const i of libres) hauteurs[i] = (durees[i]! / total) * reste;
      break;
    }
    for (const i of courtes) { hauteurs[i] = plancher; reste -= plancher; }
    libres = libres.filter((i) => !courtes.includes(i));
  }
  return hauteurs.map((h) => Math.round(h * 10) / 10);
}

export function grilleEmploiDuTemps(creneaux: CreneauImprime[]): string {
  if (creneaux.length === 0) return '<p class="edt-vide">Aucun créneau dans l’emploi du temps.</p>';

  // Les seuls jours qui ont classe : un mercredi libre n'est pas une colonne vide.
  const jours = [1, 2, 3, 4, 5, 6, 7].filter((j) => creneaux.some((c) => c.weekday === j));
  const bornes = [...new Set(creneaux.flatMap((c) => [c.startsAt.slice(0, 5), c.endsAt.slice(0, 5)]))].sort();
  const bandes = bornes.slice(0, -1).map((debut, i) => ({ debut, fin: bornes[i + 1]! }));

  // Placement : pour chaque jour, quelle bande ouvre quel créneau, et sur combien.
  const cellules = jours.map(() => new Array<{ c: CreneauImprime; span: number } | null>(bandes.length).fill(null));
  const occupe = jours.map(() => new Array<boolean>(bandes.length).fill(false));
  jours.forEach((j, i) => {
    creneaux.filter((c) => c.weekday === j)
      .sort((a, b) => a.startsAt.localeCompare(b.startsAt))
      .forEach((c) => {
        const debut = bandes.findIndex((b) => b.debut === c.startsAt.slice(0, 5));
        if (debut < 0 || occupe[i]![debut]) return; // chevauchement : le premier gagne
        let fin = bandes.findIndex((b) => b.fin === c.endsAt.slice(0, 5));
        if (fin < debut) fin = debut;
        cellules[i]![debut] = { c, span: fin - debut + 1 };
        for (let k = debut; k <= fin; k += 1) occupe[i]![k] = true;
      });
  });

  const hauteurs = hauteursDesBandes(bandes.map((b) => Math.max(1, minutes(b.fin) - minutes(b.debut))));
  const serre = HAUTEUR_MM / Math.max(1, bandes.length) < PLANCHER_MM;

  // Libellés de l'écran : la matière, sinon l'intitulé ; puis le sous-domaine.
  const contenu = (c: CreneauImprime) => {
    const principal = c.subjectName || c.label || '–';
    const second = [
      c.label && c.subjectName && c.label !== c.subjectName ? c.label : '',
      sansPrefixeNiveau(c.subjectAreaText ?? ''),
    ].filter(Boolean).join(' · ');
    return `<div class="edt-principal">${echapperHtml(principal)}</div>`
      + (second && !serre ? `<div class="edt-second">${echapperHtml(second)}</div>` : '');
  };

  const lignes = bandes.map((b, k) => {
    const cases = jours.map((_, i) => {
      const cellule = cellules[i]![k];
      if (cellule) {
        // Comme la v1 : la cellule ENTIÈRE prend la couleur de la matière,
        // texte blanc — un créneau long remplit toute sa hauteur.
        const couleur = /^#[0-9a-f]{3,8}$/i.test(cellule.c.color) ? cellule.c.color : '#64748b';
        return `<td rowspan="${cellule.span}" class="edt-creneau" style="background:${couleur};color:white">`
          + `${contenu(cellule.c)}</td>`;
      }
      return occupe[i]![k] ? '' : '<td></td>';
    }).join('');
    const horaire = serre
      ? `${heure(b.debut)}<span> – ${heure(b.fin)}</span>`
      : `${heure(b.debut)}<br><span>${heure(b.fin)}</span>`;
    return `<tr style="height:${hauteurs[k]}mm"><th class="edt-horaire">${horaire}</th>${cases}</tr>`;
  }).join('');

  const largeurHoraire = serre ? 14 : 10;
  const largeurJour = (100 - largeurHoraire) / jours.length;
  return `<table class="edt-grille${serre ? ' edt-serre' : ''}">
    <colgroup><col style="width:${largeurHoraire}%">${jours.map(() => `<col style="width:${largeurJour}%">`).join('')}</colgroup>
    <thead><tr><th>Horaire</th>${jours.map((j) => `<th>${NOMS_JOURS[j]}</th>`).join('')}</tr></thead>
    <tbody>${lignes}</tbody>
  </table>`;
}

export const STYLE_EDT = `
  table.edt-grille { width: 100% !important; table-layout: fixed; border-collapse: collapse; }
  table.edt-grille col { width: auto; }
  .edt-grille th, .edt-grille td { border: 1px solid #bbb; padding: 1mm 1.5mm; vertical-align: top;
    overflow-wrap: anywhere; }
  .edt-grille thead th { background: #2d3748; color: white; font-size: 9pt; font-weight: 700;
    text-align: center; padding: 6px; }
  .edt-horaire { background: #f4f4f5; font-size: 8pt; font-weight: 700;
    text-align: center; white-space: nowrap; line-height: 1.15; }
  .edt-horaire span { font-weight: 400; opacity: .7; }
  .edt-principal { font-size: 8.5pt; font-weight: 700; line-height: 1.15; }
  .edt-second { font-size: 7pt; line-height: 1.15; opacity: .85; }
  .edt-serre .edt-principal { font-size: 7pt; }
  .edt-serre .edt-horaire { font-size: 6.5pt; }
  .edt-grille tr { break-inside: avoid; }
  .edt-vide { color: #777; font-style: italic; }
`;
